import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Col, Row } from 'react-bootstrap-5';

import PageLibrary from './page_library';
import DefaultPages from './default_pages';
import TemplateDefaultPages from './template_default_pages';
import ToggleOverrideSection from '../shared/toggle_override_section';
import {
  addAvailablePages,
  addDefaultPage,
  clearDefaultPages,
  clearPageLibrary,
  removeDefaultPage,
  removeLibraryPage,
  reorderDefaultPage,
  reorderLibraryPage,
  setDefaultPages,
  setLibraryPages
} from '../store/actions';
import { uploadPdfTemplates } from '../api';

import AvailablePages from './available_pages';
import UploadPdfModal from './upload_pdf_modal';
import { nanoid } from 'nanoid';
import { type DefaultPage, type FormData, type Page } from '../types';

interface PagePickerProps {
  addAvailablePages: (pages: Page[]) => void;
  addDefaultPage: (id: number, key: string) => void;
  availablePages: Page[];
  basePath: string;
  clearDefaultPages: () => void;
  clearPageLibrary: () => void;
  defaultPages?: DefaultPage[];
  formData: FormData;
  removeDefaultPage: (key: string) => void;
  removeLibraryPage: (key: string) => void;
  reorderDefaultPage: (startIndex: number, endIndex: number) => void;
  reorderLibraryPage: (startIndex: number, endIndex: number) => void;
  setDefaultPages: (pages: DefaultPage[]) => void;
  setLibraryPages: (pages: Array<{ id: number; key: string; template_type?: string }>) => void;
  templateDefaultPageLibrary?: Page[];
  templateDefaultPages: DefaultPage[];
  userCreated?: boolean;
}

export function PagePicker({
  addAvailablePages,
  addDefaultPage,
  availablePages,
  basePath,
  clearDefaultPages,
  clearPageLibrary,
  defaultPages,
  formData,
  removeDefaultPage,
  removeLibraryPage,
  reorderDefaultPage,
  reorderLibraryPage,
  setDefaultPages,
  setLibraryPages,
  templateDefaultPageLibrary,
  templateDefaultPages,
  userCreated
}: PagePickerProps) {

  const [showUploadPdfModal, setShowUploadPdfModal] = useState(false);

  const handleUseDefaults = () => {
    clearDefaultPages();
    clearPageLibrary();
  };

  const handleOverrideDefaults = () => {
    setDefaultPages([...templateDefaultPages]);
    setLibraryPages((templateDefaultPageLibrary ?? []).map(page => (
      { id: page.id, key: nanoid(), template_type: page.template_type }
    )));
  };

  const handleUploadPdf = () => {
    setShowUploadPdfModal(true);
  };

  const handleUploadPdfFiles = async (files: File[]) => {
    const uploadedTemplates = await uploadPdfTemplates(basePath, files);
    addAvailablePages(uploadedTemplates);
  };

  const locked = !defaultPages;

  return (
    <div>
      <ToggleOverrideSection
        checked={!defaultPages}
        className="border-bottom pb-3"
        name="page-option-override"
        subtitle={!defaultPages ?
          'Unchecking will allow you to customize which pages appear in this document' :
          'Checking this will use Buildout\'s default pages for this document'
        }
        title="Use Buildout Default Pages"
        onOverride={handleOverrideDefaults}
        onRemove={handleUseDefaults}
      />
      <AvailablePages
        basePath={basePath}
        locked={locked}
        onUploadPdf={handleUploadPdf}
      />
      <Row>
        <Col md={6}>
          <PageLibrary
            addDefaultPage={addDefaultPage}
            availablePages={availablePages}
            locked={locked}
            pages={formData.availablePageTemplateTreeIds}
            removeLibraryPage={removeLibraryPage}
            templateDefaultPageLibrary={templateDefaultPageLibrary}
            onReorderPage={reorderLibraryPage}
          />
        </Col>
        <Col md={6}>
          {locked && (
            <TemplateDefaultPages
              availablePages={availablePages}
              templateDefaultPages={templateDefaultPages}
            />
          )}
          {!locked && (
            <DefaultPages
              availablePages={availablePages}
              defaultPages={defaultPages}
              onRemovePage={removeDefaultPage}
              onReorderPage={reorderDefaultPage}
            />
          )}
        </Col>
      </Row>
      <UploadPdfModal
        show={showUploadPdfModal}
        onHide={() => setShowUploadPdfModal(false)}
        onUpload={handleUploadPdfFiles}
      />
    </div>
  );
}

const mapStateToProps = ({ availablePages, formData }) => ({ availablePages, formData });
const mapDispatchToProps = {
  addAvailablePages,
  addDefaultPage,
  clearDefaultPages,
  clearPageLibrary,
  removeDefaultPage,
  removeLibraryPage,
  reorderDefaultPage,
  reorderLibraryPage,
  setDefaultPages,
  setLibraryPages,
};

export default connect(mapStateToProps, mapDispatchToProps)(PagePicker);
